import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DefaultFormMultiAutocomplete } from "../../../../../../common/components/default_form_multi_autocomplete";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { FolhaPagamentoDropDown } from "../../../../domain/models/folha_pagamento_dropdown";
import { CargoDropDown } from "../../../../../../common/domain/models/cargo_dropdown";
import { ReportFolhaPagamentoDetalhadaForm, ReportFolhaPagamentoDetalhadaSchema } from "../../../schemas/FolhaPagamento_lista/report_folha_pagamento_detalhada";
import { zodResolver } from "@hookform/resolvers/zod";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";


/**
 * ReportFolhaPagamentoDetalhadaModal
 */

type Props = {
  open: boolean,
  loading: boolean,
  funcionarios: FuncionarioDropDown[],
  centroCustos: CentroCustoDropDown[],
  folhaPagamentos: FolhaPagamentoDropDown[],
  cargos: CargoDropDown[],
  onClose: () => void,
  onSubmit: (form: ReportFolhaPagamentoDetalhadaForm) => void,
};

export const ReportFolhaPagamentoDetalhadaModal = ({
  open,
  loading,
  funcionarios,
  centroCustos,
  folhaPagamentos,
  cargos,
  onClose,
  onSubmit,
}: Props) => {
  const hookForm = useForm<ReportFolhaPagamentoDetalhadaForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ReportFolhaPagamentoDetalhadaSchema),
  });

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(onSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        Relatorio Folha de Pagamento Detalhada
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={loading} message="Processando">
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <DefaultFormMultiAutocomplete
                name='folhaPagamentos'
                label='Folha de Pagamento'
                options={folhaPagamentos}
                getOptionKey={(x) => x.idFolhaPagamento}
                getOptionLabel={(x) => x.descricao}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='centroCustos'
                label='Centro de Custo'
                options={centroCustos}
                getOptionKey={(x) => x.id}
                getOptionLabel={(x) => x.title}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='funcionarios'
                label='Funcionário'
                options={funcionarios}
                getOptionKey={x => x.idFuncionario}
                getOptionLabel={(x) => x.nome}
                reactHookForm={hookForm} />

              <DefaultFormMultiAutocomplete
                name='cargos'
                label='Cargo'
                options={cargos}
                getOptionKey={(x) => x.idCargo}
                getOptionLabel={(x) => x.descricao ?? ''}
                reactHookForm={hookForm} />

              <div className="w-full flex gap-1">
                <FormDatePickerInput
                  name='dataCadastroDe'
                  label='Data Cadastro De'
                  reactHookForm={hookForm} />

                <FormDatePickerInput
                  name='dataCadastroAte'
                  label='Data Cadastro Ate'
                  reactHookForm={hookForm} />

              </div>

              <div className="w-full flex gap-1">
                <FormDatePickerInput
                  name='dataPagamentoDe'
                  label='Data Pagamento De'
                  reactHookForm={hookForm} />

                <FormDatePickerInput
                  name='dataPagamentoAte'
                  label='Data Pagamento Ate'
                  reactHookForm={hookForm} />

              </div>
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {
                loading ? (
                  <PrimaryButton
                    buttonColor="primary-green">
                    Gerando...
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    type="submit"
                    buttonColor="primary-green">
                    Gerar
                  </PrimaryButton>
                )
              }
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}